import { fmtDate, fmtDateTime, fromDateTimeInput, siteDayKey, toDateTimeInput, visitWhen } from '@/lib/dates';
import { RescheduleVisitDialog } from '@/pages/visits/RescheduleVisitDialog';
import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CalendarClock, CheckCircle2, ChevronLeft, ClipboardList, Clock, Info, Loader2, MessageSquarePlus, X, XCircle } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { visits, studies, subjects, compliance, type VisitRow, type WindowCompliance, type QueryCategory, type QueryVisibilityType } from '@/api/client';
import { toast } from 'sonner';
import { humanStatus, titleCase } from '@/lib/humanise';
import { encryptId, resolveId } from '@/lib/encryptId';
import { useHasRole } from '@/components/RoleGate';
import { useAuth } from '@/contexts/AuthContext';
import { CAN_RAISE_QUERY_ANY } from '@/lib/permissions';
import { useMyPermissions } from '@/hooks/useMyPermissions';
import { OpenAdverseEventsPrompt } from '@/pages/visits/OpenAdverseEventsPrompt';
import { VisitFormsSection } from '@/pages/visits/VisitFormsSection';
import { VisitLifecyclePanel } from '@/pages/visits/VisitLifecyclePanel';
import { VisitDocumentsPanel } from '@/pages/visits/VisitDocumentsPanel';
import { VisitIeChecklistPanel } from '@/pages/visits/VisitIeChecklistPanel';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';
import {
  NextVisitBanner,
  OutOfWindowBanner,
} from '@/pages/visits/VisitScheduleBanners';
import { studyLabel } from '@/lib/studyLabel';
import { subjectDisplayId } from '@/lib/subjectLabel';

const EXTERNAL_RAISER_ROLES = new Set(['monitor', 'auditor']);

const VISIT_ACTION_ROLES = [
  'platform_admin', 'application_admin', 'site_admin',
  'principal_investigator', 'sub_investigator', 'coordinator',
] as const;

/**
 * DREAM-21 AC4 / AC11 — Visit record detail page.
 *
 * URL param is an encrypted visit ID (same XOR scheme as study IDs).
 * Shows study title + subject screen ID (not raw UUIDs).
 */

export function VisitDetailPage() {
  const { id: encryptedParam } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Decrypt the URL param → real UUID for API calls.
  const visitId = encryptedParam ? resolveId(encryptedParam) : undefined;

  const visitQ = useQuery({
    queryKey: ['visit', visitId],
    queryFn: () => visits.getById(visitId!),
    enabled: !!visitId,
  });

  if (visitQ.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 p-16 text-ink-muted">
        <Loader2 className="h-5 w-5 animate-spin" /> Loading visit…
      </div>
    );
  }

  if (visitQ.isError || !visitQ.data) {
    return (
      <div className="space-y-4">
        <BackBar onBack={() => navigate(-1)} />
        <div className={`card flex items-start gap-2 p-4 text-sm ${errorToneClass(visitQ.error)}`}>
          {isPermissionError(visitQ.error) ? <Info className="mt-0.5 h-4 w-4 shrink-0"/> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
          {errorText(visitQ.error, 'Failed to load visit.')}
        </div>
      </div>
    );
  }

  return <VisitDetail visit={visitQ.data.visit} onBack={() => navigate(-1)} visitId={visitId!} />;
}

// ---------------------------------------------------------------------------
// Main detail view
// ---------------------------------------------------------------------------

function VisitDetail({ visit: v, onBack, visitId }: { visit: VisitRow; onBack: () => void; visitId: string }) {
  const isUnscheduled = v.visit_type === 'unscheduled' || v.unscheduled === true;
  // Compared by calendar day: a reschedule that only moved the time of day
  // is still a reschedule, but the badge is about the date people look for.
  const rescheduled =
    !!v.scheduled_at && !!v.planned_date && siteDayKey(v.scheduled_at) !== siteDayKey(v.planned_date);
  const isPlanned = v.status === 'planned' || !v.status;
  const canAct = useHasRole(VISIT_ACTION_ROLES);
  // Punch-list f4 — raise a query against this visit (or a completed
  // visit's read-only form) with study/subject/visit pre-filled.
  // RBAC grid decides (queries.create, 23 Oct 2026).
  const canRaiseQuery = useMyPermissions().holdsOperation('queries.create', CAN_RAISE_QUERY_ANY);
  const [raiseQueryOpen, setRaiseQueryOpen] = useState(false);

  // Fetch study title and subject screen ID in parallel.
  const studiesQ = useQuery({
    queryKey: ['studies-list'],
    queryFn: () => studies.list(),
    staleTime: 60_000,
  });
  const subjectQ = useQuery({
    queryKey: ['subject-detail', v.subject_id],
    queryFn: () => subjects.detail(v.subject_id),
    staleTime: 60_000,
  });

  if (studiesQ.isLoading || subjectQ.isLoading) {
    return (
      <div className="space-y-5">
        <BackBar onBack={onBack} />
        <div className="flex items-center justify-center gap-2 p-16 text-ink-muted">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading visit…
        </div>
      </div>
    );
  }

  const study = studiesQ.data?.rows.find((s) => s.study_id === v.study_id);
  const studyDisplay = study ? studyLabel(study) : `Study ${v.study_id.slice(0, 8)}`;
  const subjectLabel = subjectQ.data
    ? subjectDisplayId({
      external_ref: subjectQ.data.subject.external_ref,
      ivrf_number: subjectQ.data.subject.ivrf_number ?? subjectQ.data.wizard_state?.ivrf_number,
    })
    : v.subject_id.slice(0, 8);

  return (
    <div className="space-y-5">
      <PageHeader
        onBack={onBack}
        title={
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="truncate text-base font-semibold text-ink-primary">
              {v.visit_name ?? v.visit_label ?? 'Visit'}
            </h1>
            {isUnscheduled && (
              <span className="badge badge-warn text-xs">Unscheduled</span>
            )}
            <StatusBadge status={v.status ?? null} />
          </div>
        }
        subtitle={
          <p className="mt-0.5 text-[11px] text-ink-muted">
            Subject{' '}
            <Link to={`/subjects/${v.subject_id}`} className="text-accent-700 hover:underline">
              {subjectLabel}
            </Link>
            {' · '}
            Study{' '}
            <Link to={`/studies/${encryptId(v.study_id)}`} className="text-accent-700 hover:underline">
              {studyDisplay}
            </Link>
          </p>
        }
        actions={
          canRaiseQuery && (
            <button
              type="button"
              onClick={() => setRaiseQueryOpen(true)}
              className="inline-flex items-center gap-1.5 rounded border border-surface-border bg-white px-3 py-1.5 text-xs font-medium text-ink-primary hover:bg-surface-muted/50"
            >
              <MessageSquarePlus className="h-3.5 w-3.5" />
              Raise Query
            </button>
          )
        }
      />

      {/* Phase 8 AC19 — deviation banner + next-visit banner. Deviation
          hides itself when the flag isn't set; next-visit banner is
          subject-scoped so it appears on every visit page. */}
      <OutOfWindowBanner visit={v} />
      {v.subject_id && <NextVisitBanner subjectId={v.subject_id} />}

      {/* Phase 5 (AC3) — open AE prompt. Hides itself when there's nothing to review. */}
      {visitId && <OpenAdverseEventsPrompt visitId={visitId} />}

      {/* Phase 8 slice — PI Sign / Reopen surface. Lock-state pill,
          Sign / Reopen actions, sign-off history drawer. */}
      {visitId && <VisitLifecyclePanel visitId={visitId} />}

      {/* Phase 8 base AC15 — I/E checklist. Panel self-hides when
          the visit template has no criteria configured. */}
      {visitId && <VisitIeChecklistPanel visitId={visitId} />}

      {/* Phase 7A — eSource forms section. Renders the data-entry surface. */}
      {visitId && v.subject_id && v.study_id && (
        <VisitFormsSection
          subjectId={v.subject_id}
          visitId={visitId}
          studyId={v.study_id}
        />
      )}

      {/* Phase 8 base AC18 — per-visit documents with PI relevance. */}
      {visitId && <VisitDocumentsPanel visitId={visitId} />}

      {/* Metadata card */}
      <div className="card p-5">
        <h2 className="mb-4 text-sm font-semibold text-ink-primary">Visit details</h2>
        <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm sm:grid-cols-3">
          {/* The appointment comes first — it is the question people open this
              page to answer. `planned_date` is the protocol target and the
              window's anchor, so it stays put when a visit is rescheduled; it
              is shown beside it only when the two differ, which is the only
              time it tells the reader anything. */}
          <InfoField label="Scheduled for">
            {visitWhen(v) ? fmtDateTime(visitWhen(v)!) : '—'}
            {rescheduled && (
              <span className="ml-2 rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium text-violet-700">
                rescheduled
              </span>
            )}
          </InfoField>
          <InfoField label="Planned date">
            {v.planned_date ? fmtDate(v.planned_date) : '—'}
          </InfoField>
          <InfoField label="Actual date">
            {v.actual_date ? fmtDate(v.actual_date) : '—'}
          </InfoField>
          <InfoField label="Visit type">
            {isUnscheduled ? 'Unscheduled' : 'Scheduled'}
          </InfoField>
          {v.nominal_day != null && (
            <InfoField label="Protocol window">
              Day {v.nominal_day}
              {(v.window_minus_days != null || v.window_plus_days != null) && (
                <> ± {Math.max(v.window_minus_days ?? 0, v.window_plus_days ?? 0)} days</>
              )}
            </InfoField>
          )}
          <InfoField label="Window compliance">
            <ComplianceBadge value={v.window_compliance ?? null} />
          </InfoField>
          {v.status_reason && (
            <InfoField label="Reason">{v.status_reason}</InfoField>
          )}
        </dl>
      </div>

      {/* Data entry — planned visits, action roles only */}
      {isPlanned && canAct && <RecordActualForm visitId={visitId} visit={v} />}

      {/* Status actions — planned visits, action roles only */}
      {isPlanned && canAct && <SetStatusPanel visitId={visitId} />}

      {raiseQueryOpen && (
        <RaiseVisitQueryDialog
          visitId={visitId}
          studyId={v.study_id}
          subjectId={v.subject_id}
          visitLabel={v.visit_name ?? v.visit_label ?? 'Visit'}
          onClose={() => setRaiseQueryOpen(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Punch-list f4 — raise a query pre-filled with study/subject/visit
// ---------------------------------------------------------------------------

const VISIT_QUERY_CATEGORY_LABELS: Record<QueryCategory, string> = {
  missing_document: 'Missing document',
  incorrect_metadata: 'Incorrect metadata',
  expired_document: 'Expired document',
  compliance_flag: 'Compliance flag',
  other: 'Other',
};

function RaiseVisitQueryDialog({
  visitId, studyId, subjectId, visitLabel, onClose,
}: {
  visitId: string;
  studyId: string;
  subjectId: string;
  visitLabel: string;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [category, setCategory] = useState<QueryCategory>('other');
  const [body, setBody] = useState('');
  // Defaults to how the server would derive it for this caller's role
  // (deriveOrigin), but is an explicit, overridable choice here rather
  // than a silent guess.
  const [visibility, setVisibility] = useState<QueryVisibilityType>(
    user && EXTERNAL_RAISER_ROLES.has(user.role) ? 'external' : 'internal',
  );
  const isExternalRaiser = !!user && EXTERNAL_RAISER_ROLES.has(user.role);
  const visibilityMismatch =
    (isExternalRaiser && visibility === 'internal') ||
    (!isExternalRaiser && visibility === 'external');

  const raise = useMutation({
    mutationFn: () =>
      compliance.raiseQuery({
        study_id: studyId,
        subject_id: subjectId,
        visit_id: visitId,
        category,
        body: body.trim(),
        section_ref: visitLabel,
        origin: visibility === 'external' ? 'cra_query' : 'internal',
      }),
    onSuccess: (r) => {
      toast.success(`Query ${r.query_id.slice(0, 8)}… raised against this visit.`);
      qc.invalidateQueries({ queryKey: ['compliance-queries'] });
      onClose();
    },
    onError: (err) => toast.error(errorText(err, 'Failed to raise query')),
  });

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center p-4 !m-0"
      role="dialog"
      aria-modal="true"
      aria-labelledby="raise-visit-query-title"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative w-full max-w-md rounded-lg border border-surface-border bg-surface-card shadow-xl">
        <header className="flex items-start justify-between border-b border-surface-border px-4 py-3">
          <div className="min-w-0">
            <h3 id="raise-visit-query-title" className="flex items-center gap-2 text-sm font-semibold text-ink-primary">
              <MessageSquarePlus className="h-4 w-4 text-accent-700" />
              Raise a query
            </h3>
            <p className="mt-0.5 truncate text-[11px] text-ink-muted">Visit · {visitLabel}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 text-ink-muted hover:bg-surface-muted/50" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="space-y-3 px-4 py-3">
          <label className="block text-xs">
            <span className="font-medium text-ink-secondary">Category</span>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as QueryCategory)}
              className="mt-1 w-full rounded border border-surface-border px-3 py-1.5 text-sm"
            >
              {(Object.keys(VISIT_QUERY_CATEGORY_LABELS) as QueryCategory[]).map((c) => (
                <option key={c} value={c}>{VISIT_QUERY_CATEGORY_LABELS[c]}</option>
              ))}
            </select>
          </label>

          <fieldset className="text-xs">
            <legend className="font-medium text-ink-secondary">Query type</legend>
            <div className="mt-1 flex gap-4">
              <label className="inline-flex items-center gap-1.5">
                <input
                  type="radio"
                  name="visit-query-visibility"
                  checked={visibility === 'internal'}
                  onChange={() => setVisibility('internal')}
                />
                Internal
              </label>
              <label className="inline-flex items-center gap-1.5">
                <input
                  type="radio"
                  name="visit-query-visibility"
                  checked={visibility === 'external'}
                  onChange={() => setVisibility('external')}
                />
                External
              </label>
            </div>
            {visibilityMismatch && (
              <p className="mt-1.5 rounded border border-danger-200 bg-danger-50 px-2 py-1.5 text-[11px] text-danger-800">
                {isExternalRaiser
                  ? 'Monitors/auditors can only raise External queries.'
                  : 'Site staff can only raise Internal queries.'}
              </p>
            )}
          </fieldset>

          <label className="block text-xs">
            <span className="font-medium text-ink-secondary">Description</span>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              maxLength={2000}
              placeholder="Describe the issue with this visit…"
              className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
            />
          </label>
        </div>

        <footer className="flex justify-end gap-2 border-t border-surface-border px-4 py-3">
          <button type="button" onClick={onClose} className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => raise.mutate()}
            disabled={!body.trim() || raise.isPending || visibilityMismatch}
            className="rounded bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700 disabled:opacity-50"
          >
            {raise.isPending ? 'Raising…' : 'Raise query'}
          </button>
        </footer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Record actual date form (AC11)
// ---------------------------------------------------------------------------

function RecordActualForm({ visitId, visit: v }: { visitId: string; visit: VisitRow }) {
  const qc = useQueryClient();
  // Bug found live (UAT): planned_date arrives from Caspio as a
  // timezone-less string (e.g. "2026-09-09T00:00:00"). Routing it through
  // `new Date(...)` treats it as LOCAL time, then `.toISOString()`
  // re-expresses it in UTC — for any timezone ahead of UTC (e.g. IST)
  // that silently rolls the date back a day before the form even opens.
  // Date AND time. The visit is a real appointment, and the calendar already
  // renders a time for it — until now that time came from the enrolment
  // timestamp because nothing could set one. toDateTimeInput puts the value on
  // the SITE's clock, which is the clock everything else displays in.
  const [actualDate, setActualDate] = useState(() =>
    toDateTimeInput(v.planned_date ?? v.scheduled_at ?? new Date().toISOString()),
  );
  const [notes, setNotes] = useState('');
  const [rescheduleOpen, setRescheduleOpen] = useState(false);

  const mut = useMutation({
    mutationFn: () =>
      visits.recordActual(visitId, {
        actual_date: fromDateTimeInput(actualDate),
        notes: notes.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success('Visit date recorded');
      qc.invalidateQueries({ queryKey: ['visit', visitId] });
      // Recording the date can make the visit ready for PI signature.
      qc.invalidateQueries({ queryKey: ['visit', 'sign-history', visitId] });
      qc.invalidateQueries({ queryKey: ['visits-cal'] });
      qc.invalidateQueries({ queryKey: ['workspace'] });
    },
    onError: (err) =>
      toast.error(errorText(err, 'Failed to record visit')),
  });

  return (
    <div className="card p-5">
      <RescheduleVisitDialog visit={v} open={rescheduleOpen} onClose={() => setRescheduleOpen(false)} />
      <div className="mb-4 flex items-center gap-2">
        <ClipboardList className="h-4 w-4 text-ink-muted" />
        <h2 className="text-sm font-semibold text-ink-primary">Record visit date</h2>
        {/* Rescheduling moves the appointment; recording says it happened.
            Only offered while the visit is still upcoming. */}
        {!v.actual_date && !v.completed_at && (
          <button
            type="button"
            className="btn-secondary ml-auto text-xs"
            onClick={() => setRescheduleOpen(true)}
          >
            <CalendarClock className="h-3.5 w-3.5" /> Reschedule
          </button>
        )}
      </div>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); mut.mutate(); }}>
        <div>
          <label className="label text-xs">Actual visit date and time *</label>
          <input
            type="datetime-local"
            className="input"
            required
            value={actualDate}
            onChange={(e) => setActualDate(e.target.value)}
          />
          <p className="mt-1 text-[10px] text-ink-muted">
            Pre-populated to the planned date. Change if the visit occurred at a different time.
            The visit goes to the PI for signature once this date is recorded and all its forms are submitted.
          </p>
        </div>
        <div>
          <label className="label text-xs">Notes (optional)</label>
          <textarea
            className="input min-h-[80px]"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={2000}
            placeholder="Any observations, deviations, or notes about this visit…"
          />
        </div>
        <div className="flex justify-end">
          <button type="submit" className="btn-primary" disabled={mut.isPending}>
            {mut.isPending
              ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</>
              : <><CheckCircle2 className="h-4 w-4" /> Record visit date</>}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Set status panel (Missed / Cancelled)
// ---------------------------------------------------------------------------

function SetStatusPanel({ visitId }: { visitId: string }) {
  const qc = useQueryClient();
  const [active, setActive] = useState<'missed' | 'cancelled' | null>(null);
  const [reason, setReason] = useState('');

  const mut = useMutation({
    mutationFn: (s: 'missed' | 'cancelled') =>
      visits.setStatus(visitId, { status: s, reason: reason.trim() }),
    onSuccess: (_d, s) => {
      toast.success(`Visit marked ${s}`);
      qc.invalidateQueries({ queryKey: ['visit', visitId] });
      qc.invalidateQueries({ queryKey: ['visits-cal'] });
      setActive(null);
      setReason('');
    },
    onError: (err) =>
      toast.error(errorText(err, 'Failed to update status')),
  });

  return (
    <div className="card p-5">
      <div className="mb-4 flex items-center gap-2">
        <XCircle className="h-4 w-4 text-ink-muted" />
        <h2 className="text-sm font-semibold text-ink-primary">Mark visit as missed or cancelled</h2>
      </div>
      {active === null ? (
        <div className="flex gap-2">
          <button type="button" className="btn-secondary text-xs" onClick={() => setActive('missed')}>Missed…</button>
          <button type="button" className="btn-secondary text-xs" onClick={() => setActive('cancelled')}>Cancelled…</button>
        </div>
      ) : (
        <div className="space-y-3">
          <label className="label text-xs">Reason for marking as {active} *</label>
          <textarea
            className="input min-h-[60px]"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={2000}
            placeholder="Required — at least 3 characters"
            autoFocus
          />
          <div className="flex gap-2">
            <button type="button" className="btn-ghost text-xs"
              onClick={() => { setActive(null); setReason(''); }}>Cancel</button>
            <button type="button" className="btn-primary text-xs"
              disabled={mut.isPending || reason.trim().length < 3}
              onClick={() => mut.mutate(active)}>
              {mut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Confirm'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

function BackBar({ onBack }: { onBack: () => void }) {
  return (
    <button type="button" onClick={onBack}
      className="flex items-center gap-1.5 text-sm text-ink-secondary hover:text-ink-primary">
      <ChevronLeft className="h-4 w-4" /> Back
    </button>
  );
}

function InfoField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wider text-ink-muted">{label}</dt>
      <dd className="mt-0.5 text-sm text-ink-primary">{children}</dd>
    </div>
  );
}

function StatusBadge({ status }: { status: string | null }) {
  if (!status || status === 'planned') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-surface-muted px-2 py-0.5 text-[10px] font-medium text-ink-secondary">
        <Clock className="h-3 w-3" /> Planned
      </span>
    );
  }
  const cls =
    status === 'completed' ? 'bg-success-50 text-success-700'
      : status === 'missed' ? 'bg-warning-50 text-warning-700'
        : status === 'cancelled' ? 'bg-danger-50 text-danger-700'
          : 'bg-surface-muted text-ink-secondary';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${cls}`}>
      {humanStatus(status)}
    </span>
  );
}

const COMPLIANCE_META: Record<string, { label: string; cls: string }> = {
  in_window: { label: 'In window', cls: 'bg-success-50 text-success-700' },
  early: { label: 'Early', cls: 'bg-warning-50 text-warning-700' },
  late: { label: 'Late', cls: 'bg-danger-50 text-danger-700' },
  excluded: { label: 'Excluded', cls: 'bg-surface-muted text-ink-muted' },
};

function ComplianceBadge({ value }: { value: WindowCompliance | null }) {
  if (!value) return <>—</>;
  const meta = COMPLIANCE_META[value] ?? { label: titleCase(value), cls: 'bg-surface-muted text-ink-secondary' };
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${meta.cls}`}>
      {meta.label}
    </span>
  );
}
